import { faCircleCheck, faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, CircularProgress, Grid, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NewUserBlock } from "./styles";
import { UserDTO, UserRef, UserSTO } from "../../models/user";
import { UserByEmailFilter, UserByEmailDTO } from "../../models/user-by-email";
import { UsersApiService } from "../../services/users";
import { EMAIL_PATTERN, isValidString } from "../../utils/validation/isValidString";

interface Props {
  user: UserRef | null;
  handleUser: (user: UserDTO | null) => void;
  handleNewUser: (user: UserSTO | null) => void;
  customTRP?: string;
  userAlreadyReferenced?: boolean;
  customContainerStyle?: React.CSSProperties;
  customNewUserBlockStyle?: React.CSSProperties;
  displayLabel?: boolean;
  displayWarning?: boolean;
  displayUserAvailabilityHints?: boolean;
}

interface State {
  loading: boolean;
  email: string;
  error: string;
  askForAddingNewUser: boolean;
  newUser: UserDTO | null;
  // newUserName: string;
  // newUserLastName: string;
}

const FindOrCreateUser = ({
  user,
  handleUser,
  handleNewUser,
  customTRP,
  userAlreadyReferenced,
  customContainerStyle,
  customNewUserBlockStyle,
  displayLabel,
  displayWarning = true,
  displayUserAvailabilityHints = true,
}: Props) => {
  const tRP = "components.find_or_create_user";
  const { t } = useTranslation();
  const theme = useTheme();

  const DEFAULT_USER: Partial<UserDTO> = {
    id: "",
    isDeleted: false,
    email: "",
    firstName: "",
    lastName: "",
  };

  const [state, setState] = useState<State>({
    loading: false,
    email: user?.email ?? "",
    error: "",
    askForAddingNewUser: false,
    newUser: null,
    // newUserName: "",
    // newUserLastName: "",
  });

  // #region handle change new user
  const handleChangeNewUser = useCallback((field: keyof UserDTO, value: UserDTO[keyof UserDTO]) => {
    setState((previousState) => ({
      ...previousState,
      newUser: {
        ...previousState.newUser,
        [field]: value,
      } as UserDTO,
    }));
  }, []);
  // #endregion

  // #region check if user exists
  const loadUserByEmailCallback = useCallback(async () => {
    setState((previousState) => ({ ...previousState, loading: true }));
    try {
      const service = new UsersApiService();
      service.useLoadItemsPath("/GetUserByEmail");

      const response = await service.loadItems({
        email: state.email.trim().toLowerCase(),
      } as UserByEmailFilter);

      if (response.error) {
        setState((previousState) => ({ ...previousState, error: response.error }));
      } else {
        if (response.items.length > 0 && (response.items[0] as UserByEmailDTO).user) {
          handleUser((response.items[0] as UserByEmailDTO).user as UserDTO);
          setState((previousState) => ({
            ...previousState,
            askForAddingNewUser: false,
            newUser: null,
          }));
        } else {
          handleUser(null);
          setState((previousState) => ({
            ...previousState,
            askForAddingNewUser: true,
            newUser: { ...DEFAULT_USER, email: state.email } as UserDTO,
          }));
        }
      }
    } catch (error) {
      setState((previousState) => ({ ...previousState, error: error as string }));
    } finally {
      setState((previousState) => ({ ...previousState, loading: false }));
    }
  }, [state.email, handleUser, DEFAULT_USER]);
  // #endregion

  useEffect(() => {
    if (!state.email) {
      handleNewUser(null);
      return;
    } else if (state.email && isValidString(EMAIL_PATTERN, state.email)) {
      const fetchUserByEmail = setTimeout(() => {
        loadUserByEmailCallback();
      }, 500);

      return () => clearTimeout(fetchUserByEmail);
    }
  }, [state.email]);

  useEffect(() => {
    handleNewUser(state.newUser);
  }, [state.newUser]);

  return (
    <Grid
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: "10px",
        gap: "10px",
        ...customContainerStyle,
      }}
    >
      <TextField
        label={displayLabel ? `${t(`${customTRP ?? tRP}.user_email_address`)}` : ""}
        value={state.email}
        onChange={(e) =>
          setState((previousState) => ({
            ...previousState,
            email: e.target.value,
            user: null,
            ...(!isValidString(EMAIL_PATTERN, e.target.value) && {
              askForAddingNewUser: false,
              newUser: null,
            }),
          }))
        }
        style={{ width: "300px" }}
        InputProps={{
          ...(displayUserAvailabilityHints && {
            endAdornment: (
              <>
                {!!state.email && !user && !state.loading && isValidString(EMAIL_PATTERN, state.email) ? (
                  <Tooltip title={`${t(`${customTRP ?? tRP}.user_email_address`)}`}>
                    <FontAwesomeIcon
                      icon={faCircleExclamation}
                      color={theme.palette.error.main}
                    />
                  </Tooltip>
                ) : (
                  !!state.email &&
                  user &&
                  !state.loading &&
                  !state.askForAddingNewUser && (
                    <Tooltip title={`${t(`${tRP}.user_found`)}`}>
                      <FontAwesomeIcon
                        icon={faCircleCheck}
                        color={theme.palette.success.main}
                      />
                    </Tooltip>
                  )
                )}
                {state.loading && (
                  <Tooltip title={`${t(`${tRP}.checking_if_already_exists`)}`}>
                    <CircularProgress color="primary" />
                  </Tooltip>
                )}
              </>
            ),
          }),
        }}
        placeholder={`${t(`${customTRP ?? tRP}.enter_email_address`)}`}
        error={!!state.email && (!isValidString(EMAIL_PATTERN, state.email) || userAlreadyReferenced)}
        helperText={
          state.email ? (
            userAlreadyReferenced ? (
              <Typography
                variant="body2"
                style={{ color: theme.palette.error.main }}
              >{`${t(`${customTRP ?? tRP}.user_already_in_the_list`)}`}</Typography>
            ) : (
              !isValidString(EMAIL_PATTERN, state.email) && (
                <Typography
                  variant="body2"
                  style={{ color: theme.palette.error.main }}
                >{`${t(`${tRP}.email_address_hint`)}`}</Typography>
              )
            )
          ) : (
            ""
          )
        }
      />

      {state.askForAddingNewUser ? (
        <>
          <NewUserBlock style={{ ...customNewUserBlockStyle }}>
            {displayWarning && <Alert severity="warning">{`${t(`${tRP}.unknown_user`)}`}</Alert>}
            <TextField
              value={state.newUser?.firstName}
              onChange={(e) => handleChangeNewUser("firstName", e.target.value)}
              label={`${t(`${customTRP ?? tRP}.user_first_name`)}`}
            />
            <TextField
              value={state.newUser?.lastName}
              onChange={(e) => handleChangeNewUser("lastName", e.target.value)}
              label={`${t(`${customTRP ?? tRP}.user_last_name`)}`}
            />
          </NewUserBlock>
        </>
      ) : (
        <></>
      )}
    </Grid>
  );
};

export default FindOrCreateUser;
